import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MessageSquareText, QrCode, Clock, MapPin, Home, DollarSign, Calendar, CheckCircle, MessageCircle, ClipboardCheck, Share2, Phone, Mail, MessageSquare, X, Ban, Award } from "lucide-react";
import { formatDate } from "@/utils/format";
import { ApiClient } from "@/services/api";
import { PageHeader, Card, StatusBadge, Button, LoadingSpinner, Alert } from "@/components/shared";
import { AgentResponseCard } from "@/components/owner/AgentResponseCard";
import { SelectionConfirmModal } from "@/components/owner/SelectionConfirmModal";
import { QRUnlockGenerator } from "@/components/owner/QRUnlockGenerator";
import { ContactShareModal } from "@/components/owner/ContactShareModal";
import { MessageThread } from "@/components/shared/MessageThread";
import { useMessages } from "@/hooks/useMessages";
import {
  RequestStatus,
  RequestStatusLabels,
  ProvinceLabels,
  PropertyTypeLabels,
  TimelineLabels,
  type OwnerRequest,
  type Selection,
  SelectionStatus,
} from "@/types";

const MAX_SELECTIONS = 5;

// Owner-side inline message thread for a lead
function OwnerMessageThread({ leadId, leadStatus }: { leadId: string; leadStatus?: string | null }) {
  const { messages, loading, sending, error, sendMessage, markRead } = useMessages({ leadId, enabled: true });
  const readOnly = leadStatus === "completed" || leadStatus === "expired" || leadStatus === "blocked";

  if (loading) return <div className="py-4"><LoadingSpinner /></div>;
  if (error) return <div className="text-red-400 text-sm py-2">{error}</div>;

  return (
    <div className="mt-2 border border-white/10 rounded-lg bg-midnight/50 overflow-hidden">
      <MessageThread
        messages={messages}
        currentRole="owner"
        onSend={sendMessage}
        sending={sending}
        readOnly={readOnly}
        onMarkRead={markRead}
      />
    </div>
  );
}

function getStatusVariant(status: RequestStatus) {
  switch (status) {
    case RequestStatus.Draft: return "default" as const;
    case RequestStatus.Active: return "info" as const;
    case RequestStatus.ComparingAgents: return "warning" as const;
    case RequestStatus.ContactUnlocked: return "success" as const;
    case RequestStatus.Completed: return "success" as const;
    case RequestStatus.Deactivated: return "error" as const;
  }
}

export default function RequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [request, setRequest] = useState<OwnerRequest | null>(null);
  const [responses, setResponses] = useState<any[]>([]);
  const [selections, setSelections] = useState<Selection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tab, setTab] = useState<"responses" | "selections">("responses");
  const [messageLeadId, setMessageLeadId] = useState<string | null>(null);
  const [shareLeadId, setShareLeadId] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const api = new ApiClient();
        // Fetch requests list and find ours
        const reqData = await api.get<{ data: OwnerRequest[] }>("/owner-requests");
        const found = reqData.data.find((r) => r.id === id);
        if (found) setRequest(found);

        // Fetch responses
        const respData = await api.get<{ data: any[] }>(`/owner-requests/${id}/responses`);
        setResponses(respData.data);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const toggleSelection = (responseId: string) => {
    setSelectedIds((prev) => {
      if (prev.includes(responseId)) return prev.filter((rid) => rid !== responseId);
      if (prev.length >= MAX_SELECTIONS) return prev;
      return [...prev, responseId];
    });
  };

  const handleConfirmSelections = async () => {
    setSubmitting(true);
    try {
      const api = new ApiClient();
      await api.post("/selections", {
        request_id: id,
        response_ids: selectedIds,
      });
      setSelectedIds([]);
      setShowConfirm(false);
      // Re-fetch to show updated invited state
      const respData = await api.get<{ data: any[] }>(`/owner-requests/${id}/responses`);
      setResponses(respData.data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
      setShowConfirm(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><LoadingSpinner /></div>;
  }

  if (!request) {
    return (
      <div className="text-center py-12">
        <p className="text-white/50">Request not found.</p>
        <Link to="/owner" className="text-sun hover:underline mt-2 inline-block">← Back to requests</Link>
      </div>
    );
  }

  const hasResponses = responses.length > 0;

  return (
    <div>
      {/* Back link */}
      <Link to="/owner" className="inline-flex items-center gap-1 text-sm text-white/50 hover:text-sun mb-4">
        <ArrowLeft className="h-4 w-4" /> Back to My Requests
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold text-white">{request.suburb}, {ProvinceLabels[request.province]}</h1>
            <StatusBadge status={RequestStatusLabels[request.status]} variant={getStatusVariant(request.status)} />
            {request.status === RequestStatus.Draft && (
              <Button
                size="sm"
                onClick={async () => {
                  const apiClient = new ApiClient();
                  await apiClient.patch(`/owner-requests/${id}`, { status: "active" });
                  setRequest({ ...request, status: RequestStatus.Active });
                }}
              >
                Publish
              </Button>
            )}
          </div>
          <p className="text-sm text-white/50">Created {formatDate(request.created_at)}</p>
        </div>
      </div>

      {/* Request details grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Card>
          <div className="flex items-center gap-2 text-white/50 text-xs mb-1">
            <Home className="h-3.5 w-3.5" /> Property Type
          </div>
          <p className="text-white font-medium">{PropertyTypeLabels[request.property_type]}</p>
        </Card>
        <Card>
          <div className="flex items-center gap-2 text-white/50 text-xs mb-1">
            <DollarSign className="h-3.5 w-3.5" /> Price Band
          </div>
          <p className="text-white font-medium">R{(request.price_band_min / 1_000_000).toFixed(1)}M – R{(request.price_band_max / 1_000_000).toFixed(1)}M</p>
        </Card>
        <Card>
          <div className="flex items-center gap-2 text-white/50 text-xs mb-1">
            <Calendar className="h-3.5 w-3.5" /> Timeline
          </div>
          <p className="text-white font-medium">{TimelineLabels[request.timeline]}</p>
        </Card>
        <Card>
          <div className="flex items-center gap-2 text-white/50 text-xs mb-1">
            <MessageSquareText className="h-3.5 w-3.5" /> Responses
          </div>
          <p className="text-white font-medium text-sun">{responses.length} agent{responses.length !== 1 ? "s" : ""}</p>
        </Card>
      </div>

      {/* Seller notes */}
      {request.seller_notes && (
        <Card className="mb-8">
          <p className="text-xs text-white/50 mb-1">Your Notes</p>
          <p className="text-sm text-white/70">{request.seller_notes}</p>
        </Card>
      )}

      {error && <Alert type="error" className="mb-6">{error}</Alert>}

      {/* Responses section */}
      {!hasResponses ? (
        <Card>
          <div className="text-center py-8">
            <Clock className="h-10 w-10 text-white/20 mx-auto mb-3" />
            <p className="text-white/50 text-lg">Waiting for agent responses</p>
            <p className="text-white/30 text-sm mt-1">Agents in your area will review your request and submit proposals.</p>
          </div>
        </Card>
      ) : (() => {
        const invitedResponses = responses.filter((r: any) => r.selection);
        const invitedCount = invitedResponses.length;
        const engagingCount = invitedResponses.filter((r: any) => r.selection?.lead_status === "engaging" || (r.selection?.status === "accepted" && !r.selection?.lead_status)).length;
        const contactSharedCount = invitedResponses.filter((r: any) => r.selection?.lead_status === "contact_shared").length;
        const completedCount = invitedResponses.filter((r: any) => r.selection?.lead_status === "completed").length;
        const pendingCount = invitedCount - engagingCount - contactSharedCount - completedCount;
        const canSelect = invitedCount < MAX_SELECTIONS;

        return (
        <>
          {/* Invited status bar */}
          {invitedCount > 0 && selectedIds.length === 0 && (
            <div className="sticky top-0 z-10 bg-midnight/95 backdrop-blur border border-mint/30 rounded-lg py-3 px-4 mb-6">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-mint" />
                <p className="text-white/80 text-sm">
                  {engagingCount > 0 && (
                    <><span className="font-bold text-green-400">{engagingCount}</span> engaging</>
                  )}
                  {engagingCount > 0 && contactSharedCount > 0 && <span className="text-white/30 mx-2">&bull;</span>}
                  {contactSharedCount > 0 && (
                    <><span className="font-bold text-blue-400">{contactSharedCount}</span> contact shared</>
                  )}
                  {(engagingCount > 0 || contactSharedCount > 0) && completedCount > 0 && <span className="text-white/30 mx-2">&bull;</span>}
                  {completedCount > 0 && (
                    <><span className="font-bold text-emerald-400">{completedCount}</span> completed</>
                  )}
                  {(engagingCount > 0 || contactSharedCount > 0 || completedCount > 0) && pendingCount > 0 && <span className="text-white/30 mx-2">&bull;</span>}
                  {pendingCount > 0 && (
                    <><span className="font-bold text-mint">{pendingCount}</span> awaiting response</>
                  )}
                </p>
              </div>
            </div>
          )}

          {/* Selection bar (only when actively selecting) */}
          {selectedIds.length > 0 && (
            <div className="sticky top-0 z-10 selected-card-border rounded-lg py-3 px-4 mb-6">
              <div className="flex items-center justify-between">
                <p className="text-white/70 text-sm">
                  <span className="font-bold text-sun">{selectedIds.length}</span> agent{selectedIds.length > 1 ? "s" : ""} selected
                  <span className="text-white/30 ml-2">(max {MAX_SELECTIONS})</span>
                </p>
                <Button onClick={() => setShowConfirm(true)}>
                  Confirm Selection →
                </Button>
              </div>
            </div>
          )}

          <h2 className="text-lg font-semibold text-white mb-4">Agent Responses</h2>
          <div className="grid gap-6 md:grid-cols-2">
            {responses.map((response: any) => (
              <div key={response.id}>
                <AgentResponseCard
                  response={response}
                  onSelect={canSelect ? toggleSelection : undefined}
                  onDismiss={response.selection ? async (responseId: string) => {
                    if (!confirm(`Dismiss ${response.agent.display_name}? This will remove the invitation and any messages.`)) return;
                    try {
                      const api = new ApiClient();
                      await api.delete(`/selections/${response.selection.id}`);
                      const respData = await api.get<{ data: any[] }>(`/owner-requests/${id}/responses`);
                      setResponses(respData.data);
                    } catch (err: any) {
                      setError(err.message);
                    }
                  } : undefined}
                  onReinvite={response.selection && (response.selection.status === "declined" || response.selection.status === "expired") ? async () => {
                    try {
                      const api = new ApiClient();
                      await api.post(`/selections/${response.selection.id}/reinvite`, {});
                      const respData = await api.get<{ data: any[] }>(`/owner-requests/${id}/responses`);
                      setResponses(respData.data);
                    } catch (err: any) {
                      setError(err.message);
                    }
                  } : undefined}
                  isSelected={selectedIds.includes(response.id)}
                  isInvited={!!response.selection}
                  selectionStatus={response.selection?.status || null}
                  leadStatus={response.selection?.lead_status || null}
                  selectionDisabled={invitedCount + selectedIds.length >= MAX_SELECTIONS}
                />
                {/* Message toggle for engaging agents */}
                {response.selection?.status === "accepted" && (
                  <div className="mt-2">
                    {/* Shared contact details with revoke */}
                    {response.selection.contact_shares?.length > 0 && (
                      <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-3 mb-2 space-y-1.5">
                        <p className="text-xs font-medium text-blue-400 flex items-center gap-1.5"><ClipboardCheck className="h-3.5 w-3.5" /> Shared with this agent</p>
                        <div className="flex flex-wrap gap-2">
                          {response.selection.contact_shares.map((cs: any) => (
                            <span key={cs.id} className="inline-flex items-center gap-1.5 px-2 py-1 bg-blue-500/10 rounded text-xs text-blue-300">
                              {cs.method === "phone" && <Phone className="h-3 w-3" />}
                              {cs.method === "email" && <Mail className="h-3 w-3" />}
                              {cs.method === "whatsapp" && <MessageSquare className="h-3 w-3" />}
                              {cs.value}
                              <button
                                onClick={async () => {
                                  try {
                                    const api = new ApiClient();
                                    await api.delete(`/contacts/share/${cs.id}`);
                                    // Re-fetch responses
                                    const respData = await api.get<{ data: any[] }>(`/owner-requests/${id}/responses`);
                                    setResponses(respData.data);
                                  } catch (err: any) {
                                    setError(err.message);
                                  }
                                }}
                                className="ml-1 text-red-400/60 hover:text-red-400 transition-colors"
                                title="Revoke"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="rounded-lg border border-white/10 bg-white/[0.035] p-3 shadow-sm">
                      <div className="mb-2 flex items-center justify-between gap-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-white/45">Owner actions</p>
                        <span className="text-[11px] text-white/35">Contact is shared only by consent</span>
                      </div>
                      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                      <button
                        onClick={() => setMessageLeadId(messageLeadId === response.selection.id ? null : response.selection.id)}
                        className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md border border-gold/30 bg-gold/15 px-3 py-2 text-sm font-bold text-sun hover:border-gold/50 hover:bg-gold/20"
                        aria-expanded={messageLeadId === response.selection.id}
                      >
                        <MessageCircle className="h-4 w-4" />
                        {messageLeadId === response.selection.id ? "Hide Messages" : "Messages"}
                        {response.selection.message_count > 0 && (
                          <span className="rounded-full bg-sun px-1.5 py-0.5 text-[11px] font-black leading-none text-midnight">
                            {response.selection.message_count}
                          </span>
                        )}
                      </button>
                      <button
                        onClick={() => setShareLeadId(response.selection.id)}
                        className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md border border-blue-400/30 bg-blue-500/10 px-3 py-2 text-sm font-bold text-blue-300 hover:border-blue-400/50 hover:bg-blue-500/15"
                      >
                        <Share2 className="h-4 w-4" /> Share Contact
                      </button>
                      {/* Mark Appointed — visible when contact_shared */}
                      {response.selection.lead_status === "contact_shared" && (
                        <button
                          onClick={async () => {
                            if (!confirm(`Mark ${response.agent?.display_name || "this agent"} as appointed?`)) return;
                            try {
                              const api = new ApiClient();
                              await api.post(`/leads/${response.selection.id}/complete`, {});
                              const respData = await api.get<{ data: any[] }>(`/owner-requests/${id}/responses`);
                              setResponses(respData.data);
                            } catch (err: any) {
                              setError(err.message);
                            }
                          }}
                          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md border border-emerald-400/30 bg-emerald-500/10 px-3 py-2 text-sm font-bold text-emerald-300 hover:border-emerald-400/50 hover:bg-emerald-500/15"
                        >
                          <Award className="h-4 w-4" /> Mark Appointed
                        </button>
                      )}
                      {/* Block Agent — visible for engaging/contact_shared */}
                      {response.selection.lead_status !== "blocked" && response.selection.lead_status !== "completed" && response.selection.lead_status !== "expired" && (
                        <button
                          onClick={async () => {
                            if (!confirm(`Block ${response.agent?.display_name || "this agent"}? This will revoke shared contacts and close the conversation.`)) return;
                            try {
                              const api = new ApiClient();
                              await api.post(`/leads/${response.selection.id}/block`, {});
                              const respData = await api.get<{ data: any[] }>(`/owner-requests/${id}/responses`);
                              setResponses(respData.data);
                            } catch (err: any) {
                              setError(err.message);
                            }
                          }}
                          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md border border-red-400/20 bg-red-500/5 px-3 py-2 text-sm font-semibold text-red-300/80 hover:border-red-400/35 hover:bg-red-500/10 hover:text-red-300"
                        >
                          <Ban className="h-4 w-4" /> Block
                        </button>
                      )}
                      </div>
                    </div>
                    {messageLeadId === response.selection.id && (
                      <OwnerMessageThread leadId={response.selection.id} leadStatus={response.selection.lead_status} />
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
        );
      })()}

      {showConfirm && (
        <SelectionConfirmModal
          selectedCount={selectedIds.length}
          maxSelections={MAX_SELECTIONS}
          onConfirm={handleConfirmSelections}
          onCancel={() => setShowConfirm(false)}
          isLoading={submitting}
        />
      )}

      {shareLeadId && (
        <ContactShareModal
          leadId={shareLeadId}
          onClose={() => setShareLeadId(null)}
          onShared={async () => {
            // Re-fetch responses to reflect updated lead status
            const api = new ApiClient();
            const respData = await api.get<{ data: any[] }>(`/owner-requests/${id}/responses`);
            setResponses(respData.data);
          }}
        />
      )}
    </div>
  );
}
